/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useContext, useCallback } from "react";

// internal-deps
import {
    RenderingDetails,
    SavingsLineItemData,
    SavingsRightSectionType,
    ReviewedCartApiResponse,
    ApplyCouponsWidgetInfo,
    GiftingApplyCouponWidgetInfo,
} from "@swiggy-private/cart-types";
import { AnalyticsContext, isNonEmptyString } from "@swiggy-private/checkout-core";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useCartRendererXP } from "./useCartRendererXP";
import { useCouponSavingsActions } from "./useCouponSavingsActions";

// helpers
import {
    getFallbackCouponIcon,
    getPotentialSavingsText,
    isPaymentCouponUsecase,
    showCouponCodesListInGiftingCartSavingsCorner,
    getPotentialSavingsTextsForGiftingCart,
} from "@cart-client/helpers/Coupon";
import { getCartId } from "@cart-client/helpers/Cart";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { FTUE_FOOTER_COMMS_FOR_POR_2 } from "@cart-client/constants/Coupon";

// analytics
import { impressionDualApplicationCapability } from "@cart-client/analytics/events/SavingsCorner";
import { useGiftingCouponCodeAnalytics } from "./useGiftingCouponCodeAnalytics";
import { SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE } from "@swiggy-private/cart-ui";

export interface IProps {
    couponSavingsRenderingDetails: RenderingDetails[];
}

export interface UseCouponButtonSavingsReturnType {
    getCouponButtonSavingsItem: (renderingItemData: RenderingDetails) => SavingsLineItemData;
}

export const useCouponButtonSavingsData = (
    applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"],
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
    onSavingsLineItemLoad: (title?: string, subtitle?: string, isPaymentCouponWidget?: boolean) => void,
): UseCouponButtonSavingsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { impressionEvent } = useContext(AnalyticsContext);

    const { xpSelectiveCouponVariant } = useCartRendererXP(cartResponse);

    const { onApplyCouponSectionClick } = useCouponSavingsActions({
        applyCouponWidgetData,
        applyPaymentCouponWidgetData,
    });

    const { handleImpressionForGiftingCouponCode } = useGiftingCouponCodeAnalytics();

    const onFtueCommsNudgeLoad = useCallback(() => {
        const cartId = !isEmpty(cartResponse) ? getCartId(cartResponse) : "";
        impressionDualApplicationCapability(impressionEvent, cartId);
    }, [cartResponse, impressionEvent]);

    const getCouponButtonSavingsItem = useCallback(
        (renderingItemData: RenderingDetails): SavingsLineItemData => {
            const isPaymentCouponWidget = isPaymentCouponUsecase(renderingItemData.type);

            const title = {
                value: renderingItemData.display_text,
                showNewTag: isPaymentCouponWidget,
            };

            let potentialSavingsText = getPotentialSavingsText(
                cartResponse,
                applyCouponWidgetData,
                xpSelectiveCouponVariant,
            );
            const potentialSavingsGiftingCartText = getPotentialSavingsTextsForGiftingCart(
                applyCouponWidgetData as GiftingApplyCouponWidgetInfo,
            );
            potentialSavingsText = isNonEmptyString(potentialSavingsGiftingCartText)
                ? potentialSavingsGiftingCartText
                : potentialSavingsText;

            const subtitle = isNonEmptyString(potentialSavingsText)
                ? {
                      value: potentialSavingsText,
                  }
                : undefined;

            const rightSection = {
                type: SavingsRightSectionType.ICON,
            };

            let couponCodeList;
            if (showCouponCodesListInGiftingCartSavingsCorner(applyCouponWidgetData)) {
                couponCodeList = {
                    applied: false,
                    codes: (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes,
                    impressionEventCallback: handleImpressionForGiftingCouponCode,
                };
            }

            return {
                icon: renderingItemData.icon || getFallbackCouponIcon(renderingItemData.type),
                title,
                subtitle,
                rightSection,
                onLineItemClick: () => onApplyCouponSectionClick({ isPaymentCouponWidget }),
                onLoad: () => {
                    onSavingsLineItemLoad(title.value, subtitle?.value, isPaymentCouponWidget);
                },
                footerType: isPaymentCouponWidget
                    ? SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE.TOOLTIP
                    : SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE.FOOTER_COMMS,
                footerCommsStrip: isPaymentCouponWidget
                    ? {
                          ...FTUE_FOOTER_COMMS_FOR_POR_2,
                          onLoad: onFtueCommsNudgeLoad,
                      }
                    : applyCouponWidgetData?.potential_coupon_info,
                couponCodeList,
            };
        },
        [
            applyCouponWidgetData,
            cartResponse,
            onApplyCouponSectionClick,
            onFtueCommsNudgeLoad,
            onSavingsLineItemLoad,
            xpSelectiveCouponVariant,
            handleImpressionForGiftingCouponCode,
        ],
    );

    return { getCouponButtonSavingsItem };
};
